import React, { useEffect, useState } from 'react';
import { resolvePublicFileUrl } from '../../utils/imageUrls';

type ModPhotoFieldProps = {
  /** Existing saved photo URL (edit mode). */
  existingUrl?: string | null;
  value: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
};

/**
 * Single optional photo for a modification (v1: one image per part).
 */
export const ModPhotoField: React.FC<ModPhotoFieldProps> = ({
  existingUrl,
  value,
  onChange,
  disabled = false,
}) => {
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const shown = preview || (existingUrl ? resolvePublicFileUrl(existingUrl) || existingUrl : null);

  return (
    <div>
      <label className="input-label">Photo (optional)</label>
      <p className="text-meta mb-2">One photo per part. JPG, PNG, or WebP.</p>
      <div className="flex flex-wrap items-start gap-3">
        {shown ? (
          <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-white/[0.08] bg-[#0a0a0a] shrink-0">
            <img src={shown} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-24 h-24 rounded-lg border border-dashed border-white/[0.08] bg-[#0c0c0c]/50 flex items-center justify-center text-meta text-xs shrink-0">
            No photo
          </div>
        )}
        <div className="flex flex-col gap-2 min-w-0">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            disabled={disabled}
            className="block w-full text-sm text-gray-400 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-[#141414] file:text-gray-200 file:cursor-pointer hover:file:bg-[#161616]"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              onChange(file);
            }}
          />
          {(value) && (
            <button
              type="button"
              disabled={disabled}
              className="btn-ghost btn-sm self-start"
              onClick={() => onChange(null)}
            >
              Clear selection
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModPhotoField;
